import React, { useState } from "react";
import { Card, Button, Badge } from "react-bootstrap";
import { Dropdown } from "react-bootstrap";
import { FiEye, FiDownload } from "react-icons/fi";
import DocumentModal from "./DocumentModal";
import { exportDocument } from "./documentHelpers";
import { toast } from "react-toastify";
import SpecificAPI from "services/SpecificApi";
import { useUserStore } from "context/useUserStore";
import localization from "services/localization";

// Map tags to colors
const TAG_COLORS = {
  Meeting: "#198754", // green
  Holiday: "#ffc107", // yellow
  Event: "#dc3545", // red
  Default: "#007bff", // blue
};

export function DocumentCard({ document, onExport }) {
  const { title, createdAt, tag, content } = document;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userData } = useUserStore();

  const previewText = content
    ? content.replace(/<[^>]+>/g, "").slice(0, 100) +
      (content.length > 100 ? "..." : "")
    : "";

  const formattedDate = new Date(createdAt).toLocaleDateString();

  const updateMessages = async (updatedMsg) => {
    try {
      setLoading(true);
      const res = await SpecificAPI.updateMessages(
        {
          ...updatedMsg,
          _id: updatedMsg.id,
        },
        userData?.selected_school_id,
      );

      setMessages((prev) =>
        prev.map((m) => (m.id === updatedMsg.id ? res.data : m)),
      );
      toast.success(
        localization.message_updated || "Message updated successfully!",
      );
    } catch (error) {
      console.error("Error updating messages:", error);
      toast.error(
        localization.error_updating_message ||
          "Error updating message. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (type) => {
    exportDocument(draftContent, documentTitle, type);
  };

  const getPreviewText = (html, maxLength = 100) => {
    if (!html) return "";
    // Remove head and style
    let text = html.replace(/<head[\s\S]*?<\/head>/gi, "");
    text = text.replace(/<style[\s\S]*?<\/style>/gi, "");
    // Remove all other tags
    text = text.replace(/<[^>]+>/g, "");
    // Collapse whitespace
    text = text.replace(/\s+/g, " ").trim();
    // Truncate
    if (text.length > maxLength) text = text.slice(0, maxLength) + "...";
    return text;
  };

  return (
    <>
      <Card
        className="shadow-md hover:shadow-2xl transition-all border border-gray-200 rounded-2xl bg-white/60 backdrop-blur p-3 mb-4"
        style={{
          height: "auto",
          backgroundColor: "var(--module-card-bg)",
          color: "var(--module-card-color)",
          borderColor: "var(--module-card-border-color)",
          textAlign: "left",
        }}
      >
        <Card.Body className="flex flex-col justify-between h-full">
          {/* Header: Title + Tag Badge */}
          <div className="flex items-start justify-between mb-3">
            <h5
              className="text-lg font-bold mb-1 truncate"
              title={title}
              style={{
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "var(--module-card-color)",
              }}
            >
              {title}
            </h5>
            <Badge
              style={{
                backgroundColor: TAG_COLORS[tag] || TAG_COLORS.Default,
                color: "white",
              }}
              className="text-xs font-semibold px-3 py-2 rounded-full flex-shrink-0"
            >
              {tag}
            </Badge>
          </div>

          {/* Created Date */}
          <div className="text-sm description_text mb-3">
            {localization.created_on || "Created on"}: {formattedDate}
          </div>

          {/* Document Preview */}
          <div
            className="text-sm description_text mb-4"
            style={{
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              textAlign: "left",
            }}
          >
            {getPreviewText(content, 150)}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-auto">
            <Button
              size="sm"
              className="action-btn-outline primary flex-1 d-flex align-items-center justify-center gap-2 border transition-all rounded-pill"
              onClick={() => setShowModal(true)}
            >
              <FiEye className="h-4 w-4" />
              {localization.view || "View"}
            </Button>

            <Dropdown className="flex-1">
              <Dropdown.Toggle
                variant="outline-success"
                size="sm"
                className="w-100 d-flex align-items-center justify-center gap-2 rounded-pill"
              >
                <FiDownload className="h-4 w-4" />
                {localization.export || "Export"}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() =>
                    exportDocument(
                      document.content,
                      document.title || "AI Document",
                      "pdf",
                    )
                  }
                >
                  {localization.export_as_pdf || "Export as PDF"}
                </Dropdown.Item>

                <Dropdown.Item
                  onClick={() =>
                    exportDocument(
                      document.content,
                      document.title || "AI Document",
                      "docx",
                    )
                  }
                >
                  {localization.export_as_docx || "Export as DOCX"}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Card.Body>
      </Card>

      {/* -------------------- VIEW MODAL -------------------- */}

      <DocumentModal
        displayContent={content}
        msg={document}
        onUpdate={(updatedResponse) => {
          return updateMessages({
            ...msg,
            updated_response: updatedResponse,
          });
        }}
        loading={loading}
        setShowModal={setShowModal}
        showModal={showModal}
      />
    </>
  );
}
